import { G2S } from '../g2s';
import * as api from '../api';
import * as flights from '../flights';
import * as utils from '../utils';

export class TrackingService {
    /**
     * Tracks event in external systems.
     * @param eventName - name of the event.
     * @param eventParams - event parameters.
     * @param eventMergeParams - additional event parameters to merge with eventParams. eventParams will not be changed.
     */
    public static track(eventName: TrackEvent, eventParams: TrackParams, eventMergeParams?: TrackParams) {
        const p = utils.cloneWithSpaces(eventParams);

        if (eventMergeParams) {
            for (const attr in eventMergeParams) {
                // eslint-disable-next-line no-prototype-builtins
                if (eventMergeParams.hasOwnProperty(attr)) p[attr.replace(/([a-z](?=[A-Z]))/g, '$1 ')] = eventMergeParams[attr];
            }
        }

        if ((<any>window)._kmq) {
            (<any>window)._kmq.push(['record', eventName, p]);
        }

        if ((<any>window).mixpanel) {
            (<any>window).mixpanel.track(eventName, p);
        }

        if (G2S.isDev) {
            console.log('Track [' + eventName + ']', p);
        }
    }

    public static updateParamsFromSearch(trackParams: TrackParams, searchParams: flights.FlightSearchParams) {
        trackParams.FlightType = searchParams.roundtrip ? 'Round Trip' : 'One Way';
        trackParams.FlightDirection = searchParams.from + '-' + searchParams.to;
        trackParams.FlightClass = searchParams.class;
        trackParams.Passengers = searchParams.passengers.adults + searchParams.passengers.children + searchParams.passengers.infants;
        trackParams.PassengersAdult = searchParams.passengers.adults;
        trackParams.PassengersChildren = searchParams.passengers.children;
        trackParams.PassengersInfant = searchParams.passengers.infants;
    }

    public static updateParamsFromReservation(trackParams: TrackParams, reservation: flights.ReservationModel) {
        trackParams.FlightPrice = reservation.selectedFlight?.billingPrice;
        trackParams.OrderAmount = reservation.getTotalPrice();
        trackParams.Airline = reservation.selectedFlight!.validatingAirline.iataCode;
        trackParams.FlightCount = reservation.flights.length;
        trackParams.HasCustomBaggage = (reservation.selectedFlight && reservation.selectedFlight.customBaggage && reservation.selectedFlight.customBaggage.bookable) || false;
    }

    public static updateParamsFromReservationBeforeSubmit(trackParams: TrackParams, reservation: flights.ReservationModel) {
        trackParams.OrderAmount = reservation.getTotalPrice();
        trackParams.PaymentAdapter = reservation.payment!.selectedPaymentTypeId;
        trackParams.BonusAmount = reservation.payment!.bonusAmount;
        trackParams.CouponAmount = reservation.couponDiscountAmount;
        trackParams.ProductsPrice = reservation.getTotalProductsPrice();
        trackParams.CustomBaggagePrice = reservation.customBaggagePrice;
        trackParams.CustomPlacesPrice = reservation.getTotalPlacesPrice();
    }
}

export class TrackParams {
    Affiliate?: string;
    Airline?: string;
    BonusAmount?: number;
    CouponId?: string;
    CouponAmount?: number;
    CustomBaggagePrice?: number;
    CustomPlacesPrice?: number;
    DaysToDeparture?: number;
    ErrorReason?: string;
    FlightCount?: number; // How many flights in search results or on booking form
    FlightClass?: string;
    FlightDirection?: string;
    FlightPrice?: number;
    FlightSearchDuration?: number;
    FlightType?: string; // Round Trip or One Way
    GdsAdapter?: string;
    HasCustomBaggage?: boolean;
    InitStartDuration?: number;
    Passengers?: number;
    PassengersAdult?: number;
    PassengersChildren?: number;
    PassengersInfant?: number;
    PaymentAdapter?: string;
    ProductsPrice?: number;
    PromotionId?: string;
    OrderAmount?: number;
    OrderOpinion?: number;
    Step?: string;
}

export enum TrackEvent {
    BookingError = 'Booking Error',
    BookingInit = 'Booking Init',
    BookingOutOfStock = 'Booking Out of Stock',
    BookingPayment = 'Booking Payment',
    BookingStart = 'Booking Start',
    BookingStepBack = 'Booking Step Back',
    BookingStepPayment = 'Booking Step Payment',
    BookingStepSeats = 'Booking Step Seats',
    BookingStepServices = 'Booking Step Services',
    BookingSuccess = 'Booking Success',
    BookingSuccessLater = 'Booking Success Later',
    BookingSuccessPaid = 'Booking Success Paid',
    BookingSuccessRedirect = 'Booking Success Redirect',
    FlightSearchEmpty = 'Flight Search Empty',
    FlightSearchError = 'Flight Search Error',
    FlightSearchResults = 'Flight Search Results',
    FlightSearchStart = 'Flight Search Start',
}